/**
 * tutorialStore — the guided example-scene tour's step machine.
 *
 * Steps advance on REAL bridge acks/events, never derived state:
 *   script (the Script tab, the first scene open — the user taps the Boards tab) → intro
 *   → coverageSuggest (the board's Suggest Shots opens the dialog on the scene's own shot)
 *   → coverageReview (the dialog's OWN Suggest Shots swaps in the baked envelope) → blocking
 *   (Apply) → render (the rail's step 4, entered the moment the blocked shots are on the board)
 *   → finale (sketch applied).
 *
 * 20260907 (owner) — the tour now STARTS on the Script tab: start() arms at 'script' and
 * tutorialHandlers.startTutorial flips the route there. Only a real Boards-tab tap advances it.
 *
 * 20260904 — the canvas beat was removed: Apply no longer auto-enters the native 3D canvas, so
 * there is no 'canvas' step and no tutorialCanvasReturned verb.
 *
 * 20260921 (owner) — the finale card outlives the tour now: `finaleEncore` re-presents it on the
 * Timeline once the scene is playing, and `sketchedRestCount` remembers what the Sketch CTA
 * offered so its done state can keep that number. Both belong to the RUN, so start() clears them;
 * end() deliberately does not (see finaleEncore).
 *
 * In-memory only by design: the fresh-copy-per-entry model (spec §12) makes
 * persistence unnecessary; an app restart mid-tour just yields a normal project.
 * loadProject() never touches this store, so progress survives the live-board
 * re-pushes that follow Apply / rough-block.
 */
import { create } from 'zustand';

// 20260902 — `coverageSuggest` sits between intro and coverageReview: the dialog is open on the
// scene's own shot and the user has yet to press its Suggest Shots button (spec §14). It is a
// SEPARATE step, not a sub-state of intro, because the rail must hide and the coach must show for
// it exactly as they do for the review beat.
// 20260907 — `script` is the tour's opening beat: the Script tab with the first scene selected and
// expanded, the rail pointing at the Boards tab. It is a SEPARATE step, not a sub-state of intro,
// because the rail's card, arrow target and spotlight all differ and only a real tab tap leaves it.
export type TutorialStep = 'script' | 'intro' | 'coverageSuggest' | 'coverageReview' | 'blocking' | 'render' | 'finale';

/** Mirror of useAiWorkspaceFlow's local WorkspaceStep ('options'|'generating'|'review'|'done').
 *  TutorialRail is mounted separately from the AI workspace (SceneBoardPage vs. TimelineView)
 *  and has no other way to read which render-beat sub-state is on screen — see
 *  useAiWorkspaceFlow.ts's mirroring effect, the sole writer of this field. */
export type RenderFlowStep = 'options' | 'generating' | 'review' | 'done';

/**
 * 20260921 (owner) — the finale card's ENCORE: "after pressing Play your scene, transitioning to
 * the Timeline tab, and stepping through the Timeline tutorial cards, display the That's the whole
 * loop panel again while the scene is playing".
 *
 * It has to be its own field rather than a sixth TutorialStep, because the TOUR IS OVER by then and
 * must be: a dozen surfaces key on `active`, so "Play your scene" genuinely ends the tour before it
 * routes, exactly as it did before this flag existed. The encore is a separate, flagged
 * re-presentation of the same card on the other side of that end.
 *
 * 20260922 (owner) — Quick Orient no longer figures in this at all. It used to be gated off only
 * while `active` was true, which is why "Play your scene" had to hand playback over to the coach
 * marks; now the marks never arm inside a tutorial project (`inTutorialProject`), so the Timeline
 * this card returns to is plain, and playScene starts playback itself.
 *
 *   off     — no encore pending (the default, and what start() restores).
 *   armed   — Play your scene was pressed and the scene has not started playing yet.
 *   playing — the scene is playing and the card is HELD BACK. 20260921 (owner, after watching the
 *             first cut bring it up the moment playback began): "Bringing the panel back right
 *             away blocks the user from viewing the scene they just pressed to watch so delaying
 *             until it's done or their attention moves elsewhere makes sense." It comes up after
 *             "the scene finishes playback", "the user stops/pauses playback", or "the user taps on
 *             another tab" — TutorialRail's reveal effect owns those predicates.
 *   shown   — revealed, and it STAYS revealed (on every tab): the tour's remaining exits live on
 *             it. 20260921 (owner, tweak 6): those are now Next: Learn about casting (which hands
 *             the user straight into the Casting tour) and Start a new project — End tour is gone
 *             from this card. Both clear this flag themselves rather than waiting for the project
 *             close to be observed.
 */
export type FinaleEncore = 'off' | 'armed' | 'playing' | 'shown';

/**
 * 20260921 (owner) — ONE RAIL, TWO SCRIPTS (design §2.1). The Projects shelf's "Characters" card
 * opens a second, pre-baked tutorial project and runs a 6-panel tour of its own. It shares this
 * store, this rail slot and the same exits; what it does NOT share is the Getting started tour's
 * choreography — a characters run must behave like an ORDINARY project everywhere that flow bends
 * the app (baked coverage, free rough-block, suppressed selection, the intro/render glow), because
 * its own beats are real: a real Apply, real paid generations, a real character save.
 *
 * So every consumer of "a tour is active" is classified against this field rather than against
 * `active` alone, through the three selectors at the bottom of this file:
 *   · selectAnyTourActive       — "a tour owns the screen": keep OTHER guidance (Quick Orient,
 *                                 GuideRail, ScriptOrient, the import checklist, select mode) out
 *                                 of its way, whichever tour it is;
 *   · selectExampleTourActive   — the Getting started choreography, which only that tour may get;
 *   · selectCharactersTourActive — the characters tour's own surfaces (task W4).
 * A source-scanning test (store/__tests__/tutorialStore.activeReads.test.ts) forbids reading
 * `.active` off this store anywhere else, so the classification cannot quietly erode.
 */
export type TutorialKind = 'example' | 'characters';

/** The new character the creator handed back, as `castMemberAdded` reports it. `characterId` is
 *  null when the repush has not yet given the row an id the finale's Edit appearance can use —
 *  the name is the order-insensitive key, the id is the shortcut. */
export interface TutorialNewCharacter {
  name: string;
  characterId: number | null;
}

export interface TutorialState {
  active: boolean;
  /** Which script is running (see TutorialKind). Deliberately NOT reset by end(), for the same
   *  reason `step` is not: the encore renders the finale card with the tour already over, and the
   *  rail host has to know whose card it is. start() is the only thing that sets it. */
  kind: TutorialKind;
  /** The characters tour's only stored beat. Panels 1-3 are DERIVED in its rail from the route and
   *  the Breakdown sub-tab, so the one thing that has to be remembered is whether the creator has come
   *  back with a new character (panel 6). Meaningless while kind === 'example'. */
  charactersPhase: 'guide' | 'finale';
  /** Set by enterCharactersFinale from `castMemberAdded`'s payload.
   *  20260921 (owner, tweak set 2): its one READER — the Casting finale's Edit appearance CTA —
   *  was removed with that button, so this is written-only today. Kept because the WRITER is the
   *  live bridge handler: `characterId` is a field Unity still sends on every castMemberAdded, this
   *  is where the wire's 0/negative/absent/non-numeric cases are normalised to null, and
   *  contract.tutorial.test.ts pins that normalisation THROUGH this field. Deleting it would delete
   *  the only observable of a wire contract that has not changed. */
  newCharacter: TutorialNewCharacter | null;
  /** 20260922 (owner) — THE OPEN DOCUMENT IS A TUTORIAL COPY, whether or not a tour is running.
   *  Written from the setSqlitePath payload's `tutorial_project` on every FRESH load (Unity emits it
   *  for either tour's copy — both are minted into the same sample_02_ slot) and by start(); a
   *  refresh re-push is the same document, so it leaves the flag alone. NOT a derivative of
   *  `active`: the tour can end (End tour, the finale) with the user still standing in the tutorial
   *  project, and a WebView recreate after that reloads the page with no startTutorial at all —
   *  which is exactly the case the owner asked us to keep the first-entry coach marks out of. */
  inTutorialProject: boolean;
  /** 20261009 (owner) — THE OPEN DOCUMENT IS THE BUNDLED EXAMPLE PROJECT (SampleProject2.previs).
   *  Written from the setSqlitePath payload's `example_project` on every FRESH load, on exactly the
   *  contract `inTutorialProject` has (a refresh re-push is the same document and leaves it alone).
   *  The Example is a New-user-guide project like the tutorial copies — Unity mints it fresh on every
   *  open — so the first-entry coach marks (Quick Orient, ScriptOrient) keep out of it too: "those
   *  tutorials are for orienting users on their first custom project". NOT folded into
   *  `inTutorialProject`: that flag is also what start() latches, and a tour never runs here. */
  inExampleProject: boolean;
  step: TutorialStep;
  busy: string | null;
  sceneId: number | null;
  renderFlowStep: RenderFlowStep | null;
  /** See FinaleEncore. Deliberately NOT cleared by end() — playScene ends the tour and arms this in
   *  the same breath, so an end()-side clear would cancel the encore the instant it was armed. */
  finaleEncore: FinaleEncore;
  /** The project TITLE the encore is pinned to, captured when it is armed. The encore outlives the
   *  tour, so something has to take it away when the tutorial project is closed or replaced — and
   *  neither of the obvious candidates can: the project OBJECT is replaced on every tile batch, and
   *  projectLoadSeq is bumped by the repush that follows a sketch apply while the same project
   *  stays open. TutorialRail compares this (plus the pinned scene still being present) against the
   *  loaded project; both survive a repush of the same document and both fail on a different one. */
  finaleEncoreProject: string | null;
  /** How many shots "Sketch the other N shots" offered at the moment it was pressed, or null while
   *  it has not been pressed in this tour run. 20260921 (owner): the button no longer disappears
   *  once its work has landed — it stays in the stack, disabled and struck through — and it must go
   *  on reading the count it offered rather than the live remainder, which is 0 by then. Lives here
   *  rather than in the component because the encore re-presents the card after the tour ended (and
   *  potentially after a remount); start() is what resets it, so a new tour offers the button afresh. */
  sketchedRestCount: number | null;
  /** Arms a tour. The default keeps every existing no-argument call site (the bridge's
   *  `startTutorial` handler) on the Getting started tour, and resets the characters fields along
   *  with the example ones — this store outlives any single run. */
  start: (kind?: TutorialKind) => void;
  /** The characters tour's panel 6. A NO-OP unless a characters tour is actually running:
   *  `castMemberAdded` is sent for EVERY creator save (owner 20260921, not tour-gated), so a
   *  character saved outside this tour — or during the Getting started one — must not arm it. */
  enterCharactersFinale: (c: TutorialNewCharacter) => void;
  /** Writes `inTutorialProject`. Its one caller is the setSqlitePath handler, on FRESH loads only —
   *  a refresh re-push is the same document and must not re-answer the question. (start() sets the
   *  same field directly, for the reason given there.) */
  setInTutorialProject: (v: boolean) => void;
  /** Writes `inExampleProject`. Same one caller, same fresh-load-only rule as setInTutorialProject. */
  setInExampleProject: (v: boolean) => void;
  setStep: (s: TutorialStep) => void;
  setBusy: (b: string | null) => void;
  setSceneId: (id: number | null) => void;
  setRenderFlowStep: (s: RenderFlowStep | null) => void;
  /** Play your scene was pressed: remember the encore and the project it belongs to. */
  armFinaleEncore: (projectTitle: string | null) => void;
  /** armed → playing: the scene has started and the card is being held back for it. A no-op from
   *  any other state. */
  holdFinaleEncoreForPlayback: () => void;
  /** armed | playing → shown. A no-op from any other state, so a stray call can neither resurrect
   *  a cleared encore nor re-reveal one the user has already exited. */
  revealFinaleEncore: () => void;
  /** The encore's exits: End tour, Next: Learn about casting, and the tutorial project going away.
   *  (Start a new project was removed from that finale 20260921 — owner, tweak set 2 — and End tour
   *  came back in its place, so this set is what it was before tweak 6 minus that row.) */
  clearFinaleEncore: () => void;
  /** Records the count "Sketch the other N shots" offered, once its sketches have landed. */
  markSketchRestDone: (count: number) => void;
  end: (reason: 'skipped' | 'finished') => void;
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  active: false,
  kind: 'example',
  charactersPhase: 'guide',
  newCharacter: null,
  inTutorialProject: false,
  inExampleProject: false,
  step: 'script',
  busy: null,
  sceneId: null,
  renderFlowStep: null,
  finaleEncore: 'off',
  finaleEncoreProject: null,
  sketchedRestCount: null,
  // A fresh tour wipes the previous run's finale state along with its step: TutorialRail stays
  // mounted for the whole app session, so an encore (or a done-Sketch button) left over from the
  // last run would otherwise ride into this one.
  // 20260921 — the characters fields join that wipe for exactly the same reason, and `kind` is set
  // HERE rather than anywhere else: arming is the one moment the app knows which script is running.
  // 20260922 — `inTutorialProject` is SET here too, and is the one field in this write that is not a
  // wipe: a tour can only ever run inside a tutorial copy, so arming one is evidence of the
  // document's identity. The bridge payload is still the authority (it survives the tour's end);
  // this keeps the two agreeing without a round trip, and keeps fixtures that arm a tour with no
  // bridge at all honest. end() deliberately does NOT clear it — the document is still open.
  start: (kind = 'example') => set({
    active: true, kind, step: 'script', busy: null, inTutorialProject: true,
    finaleEncore: 'off', finaleEncoreProject: null, sketchedRestCount: null,
    charactersPhase: 'guide', newCharacter: null,
  }),
  enterCharactersFinale: (c) => {
    const s = get();
    if (!s.active || s.kind !== 'characters') return;
    set({ charactersPhase: 'finale', newCharacter: c });
  },
  setInTutorialProject: (v) => set({ inTutorialProject: v }),
  setInExampleProject: (v) => set({ inExampleProject: v }),
  setStep: (s) => set({ step: s }),
  setBusy: (b) => set({ busy: b }),
  setSceneId: (id) => set({ sceneId: id }),
  setRenderFlowStep: (s) => set({ renderFlowStep: s }),
  armFinaleEncore: (projectTitle) => set({ finaleEncore: 'armed', finaleEncoreProject: projectTitle }),
  holdFinaleEncoreForPlayback: () => { if (get().finaleEncore === 'armed') set({ finaleEncore: 'playing' }); },
  revealFinaleEncore: () => {
    const e = get().finaleEncore;
    if (e === 'armed' || e === 'playing') set({ finaleEncore: 'shown' });
  },
  clearFinaleEncore: () => set({ finaleEncore: 'off', finaleEncoreProject: null }),
  markSketchRestDone: (count) => set({ sketchedRestCount: count }),
  end: (reason) => {
    if (!get().active) return;
    set({ active: false, busy: null });
    // Dynamic import avoids a store↔bridge module cycle.
    import('@/bridge/features/tutorial').then((m) => m.sendTutorialEnded(reason));
    // The board's Enhance chip may have parked the glow hand-off flag; without an owner
    // it survives the tour and lights the workspace Enhance Shot button gold in a later,
    // non-tutorial session (where the tap is a REAL paid generation). Same dynamic-import
    // idiom to avoid a store↔store cycle.
    import('@/store/uiStore').then((m) => {
      if (m.useUiStore.getState().glowTutorialStep === 'enhance') {
        m.useUiStore.getState().setGlowTutorialStep(null);
      }
    });
  },
}));

// ─── The three tour predicates (20260921, design §2.1) ──────────────────────────────────────────
// THE ONLY sanctioned way to ask this store whether a tour is running. Nothing outside this file
// may read `.active` directly — store/__tests__/tutorialStore.activeReads.test.ts scans src/ for
// exactly that and fails the build — because "a tour is active" stopped being one question the day
// a second script joined the rail, and a bare `.active` read silently answers the WRONG one.
//
// They are plain functions of the state rather than hooks, so the same predicate serves both call
// shapes with no second opinion: `useTutorialStore(selectExampleTourActive)` where a render must
// react, `selectExampleTourActive(useTutorialStore.getState())` where a handler reads once at
// press time.

/** "A tour owns the screen." What OTHER guidance suppresses itself against — Quick Orient's
 *  auto-arm, ScriptOrient, GuideRail, the import checklist, the guide arming in the setSqlitePath
 *  handler, and the shell chrome (board select mode) whose own interaction would dead-end any
 *  tour. Never asks which script is running, and must not: a second
 *  coach mark over either tour is the same bug. */
export const selectAnyTourActive = (s: TutorialState): boolean => s.active;

/** The GETTING STARTED tour specifically. Everything that implements its choreography keys on
 *  this: the baked coverage dialog path, the free rough-block, the edits-ignored Apply, the
 *  suppressed shot selection, the intro/render glow, the board-arrival latches, the render-flow
 *  mirroring and the encore. A characters run must fall through every one of them and behave like
 *  an ordinary project. */
export const selectExampleTourActive = (s: TutorialState): boolean =>
  s.active && s.kind === 'example';

/** The CHARACTERS tour. Its rail, its Add-character tour hint and its castMemberAdded landing
 *  (task W4) are the only things that may key on this. */
export const selectCharactersTourActive = (s: TutorialState): boolean =>
  s.active && s.kind === 'characters';

/** The open document is a tutorial copy — whether or not a tour is running. What the FIRST-ENTRY
 *  coach marks (Quick Orient, ScriptOrient) gate on in addition to selectAnyTourActive: they are a
 *  reminder for the user's OWN project (owner 20260922). Not one of the three tour predicates above
 *  — it answers a question about the DOCUMENT, not about the rail — but it lives beside them because
 *  every consumer reads it in the same breath as one of them. */
export const selectInTutorialProject = (s: TutorialState): boolean => s.inTutorialProject;
/** 20261009 — the bundled Example project is open (see the field). */
export const selectInExampleProject = (s: TutorialState): boolean => s.inExampleProject;
